// FeedbackWidget — async "email-like" inbox launcher.
//
// A small floating button (💬, bottom-left) that opens a modal where
// the user can send the administrator a suggestion, bug report, or
// request for a new tool. The message is captured in the user's UI
// language; the worker stores it and (for non-PT senders) caches a
// Portuguese translation so the admin always reads in PT.
//
// Design decisions:
//
//   • Bottom-LEFT corner so it doesn't collide with the AIAssistant
//     launcher (bottom-right). Both are persistent on most pages.
//   • Same visual language as AIAssistant: dark zinc card, cyan accent.
//   • All copy localised in PT/EN/ES (and gracefully falls back to EN
//     for the other UI languages — the message body itself can be in
//     any language, the admin will read a PT translation).
//   • Anonymous-friendly: optional email field is the ONLY way to give
//     the admin a way to contact you back. Rate-limited by IP server-side.
//   • No live chat. Sending feels like clicking "send" on an email:
//     button → success state → modal can be closed.

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare, Send, X, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useI18n } from '../i18n';
import { Button } from './ui/button';
// We deliberately do NOT import lib/api.js for the POST itself: api()
// retries on 401 and we want to read the raw status (especially 429) to
// localise the error message. We still tap useAuth() so we can attach
// the access token (in-memory only) when the user is logged in.
import { useAuth } from '../auth/AuthContext';

// Don't render the launcher on admin / login / signup / configurator
// pages. Same rationale as AIAssistant: those workspaces are dense
// enough that a floating button just gets in the way. The admin can
// always access the inbox from /admin → Feedback.
const HIDE_ON_PATHS = [
  '/admin',
  '/account',
  '/login',
  '/signup',
  '/forgot',
  '/reset',
  '/verify-email',
  '/thanks',
  '/box/',
];

function shouldHide(pathname) {
  if (!pathname) return false;
  return HIDE_ON_PATHS.some((p) =>
    p.endsWith('/') ? pathname.startsWith(p) : (pathname === p || pathname.startsWith(p + '/')),
  );
}

// ─── Localised strings (PT/EN/ES). Other UI langs fall back to EN. ──
const STR = {
  launcher: { pt: 'Enviar feedback', en: 'Send feedback', es: 'Enviar comentarios' },
  title:    { pt: 'Fale com o administrador', en: 'Talk to the administrator', es: 'Habla con el administrador' },
  intro: {
    pt: 'Sugestões, problemas, ferramentas que você gostaria de ver? Mande uma mensagem — eu respondo assim que puder.',
    en: 'Suggestions, problems, tools you would like to see? Send a message — I will reply as soon as I can.',
    es: '¿Sugerencias, problemas, herramientas que te gustaría ver? Envía un mensaje — te respondo cuando pueda.',
  },
  subject:        { pt: 'Assunto',  en: 'Subject',  es: 'Asunto' },
  subjectPh: {
    pt: 'Ex.: sugestão de nova ferramenta',
    en: 'Ex.: suggestion for a new tool',
    es: 'Ej.: sugerencia de una nueva herramienta',
  },
  message:        { pt: 'Mensagem', en: 'Message',  es: 'Mensaje' },
  messagePh: {
    pt: 'Descreva sua sugestão, problema ou ideia em detalhes…',
    en: 'Describe your suggestion, problem or idea in detail…',
    es: 'Describe tu sugerencia, problema o idea en detalle…',
  },
  emailLabel:     { pt: 'E-mail (opcional)', en: 'Email (optional)', es: 'Correo (opcional)' },
  emailPh:        { pt: 'voce@exemplo.com',  en: 'you@example.com',  es: 'tu@ejemplo.com' },
  emailHint: {
    pt: 'Se você deixar um e-mail, o administrador pode entrar em contato com você.',
    en: 'If you leave an email, the administrator can get back to you.',
    es: 'Si dejas un correo, el administrador puede ponerse en contacto contigo.',
  },
  send:    { pt: 'Enviar',           en: 'Send',             es: 'Enviar' },
  sending: { pt: 'Enviando…',        en: 'Sending…',         es: 'Enviando…' },
  cancel:  { pt: 'Fechar',           en: 'Close',            es: 'Cerrar' },
  // Success view
  thanksTitle: {
    pt: 'Obrigado pela mensagem!',
    en: 'Thanks for your message!',
    es: '¡Gracias por tu mensaje!',
  },
  thanksBody: {
    pt: 'Recebi seu feedback e vou responder assim que puder. Se você informou um e-mail, fique de olho na sua caixa de entrada.',
    en: 'I received your feedback and will reply as soon as I can. If you provided an email, please keep an eye on your inbox.',
    es: 'Recibí tus comentarios y te responderé cuando pueda. Si dejaste un correo, mantén un ojo en tu bandeja de entrada.',
  },
  sendAnother: { pt: 'Enviar outra',   en: 'Send another',   es: 'Enviar otro' },
  // Errors
  errRateLimit: {
    pt: 'Muitas mensagens recentes deste IP. Tente novamente em uma hora.',
    en: 'Too many recent messages from this IP. Please try again in an hour.',
    es: 'Demasiados mensajes recientes desde esta IP. Vuelve a intentarlo en una hora.',
  },
  errEmail: {
    pt: 'O e-mail informado parece inválido.',
    en: 'The email address looks invalid.',
    es: 'El correo informado parece inválido.',
  },
  errGeneric: {
    pt: 'Não consegui enviar sua mensagem agora. Tente novamente em alguns minutos.',
    en: 'Could not send your message right now. Please try again in a few minutes.',
    es: 'No pude enviar tu mensaje ahora. Por favor, vuelve a intentarlo en unos minutos.',
  },
  // Limits
  charsRemaining: { pt: 'caracteres restantes', en: 'characters left', es: 'caracteres restantes' },
};

function s(key, lang) {
  const e = STR[key];
  if (!e) return key;
  return e[lang] || e.en || e.pt || '';
}

// Mirror server-side caps so the user gets immediate feedback.
const MAX_SUBJECT = 140;
const MAX_BODY    = 4000;

export default function FeedbackWidget() {
  const { lang } = useI18n();
  const location = useLocation();
  const auth     = useAuth();
  const hidden = shouldHide(location.pathname);

  const [open,    setOpen]    = useState(false);
  const [subject, setSubject] = useState('');
  const [body,    setBody]    = useState('');
  const [email,   setEmail]   = useState('');
  const [busy,    setBusy]    = useState(false);
  const [done,    setDone]    = useState(false);
  const [err,     setErr]     = useState('');

  // Reset state any time the modal is closed.
  useEffect(() => {
    if (!open) {
      // Small delay so the closing animation doesn't show empty fields
      // mid-transition. (We don't actually animate, but keep the UX
      // consistent if the parent ever wraps this in a transition.)
      const t = setTimeout(() => {
        setSubject(''); setBody(''); setEmail('');
        setBusy(false); setDone(false); setErr('');
      }, 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on ESC for keyboard users.
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  async function submit(e) {
    e?.preventDefault?.();
    if (!subject.trim() || !body.trim() || busy) return;

    setBusy(true);
    setErr('');

    try {
      // Use fetch directly — anon flow doesn't need api()'s 401-retry,
      // and we want to read the raw error body so the localisation can
      // distinguish rate-limit vs validation errors.
      const base = process.env.REACT_APP_API_BASE || '';
      const headers = { 'Content-Type': 'application/json' };
      // Best-effort attach bearer if logged in — lets the admin tie the
      // message to the user account. Tokens live in memory (AuthContext),
      // never in localStorage.
      const token = auth?.token;
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${base}/api/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          subject: subject.trim().slice(0, MAX_SUBJECT),
          body:    body.trim().slice(0, MAX_BODY),
          email:   email.trim() || undefined,
          lang:    (lang || 'pt').slice(0, 2).toLowerCase(),
        }),
      });

      if (res.ok) {
        setDone(true);
        return;
      }

      // Pick the localised error based on HTTP status / detail body.
      let detail = '';
      try {
        const data = await res.json();
        detail = (data?.detail || '').toLowerCase();
      } catch { /* not json */ }

      if (res.status === 429) {
        setErr(s('errRateLimit', lang));
      } else if (res.status === 400 && detail.includes('email')) {
        setErr(s('errEmail', lang));
      } else {
        setErr(s('errGeneric', lang));
      }
    } catch (e) {
      setErr(s('errGeneric', lang));
    } finally {
      setBusy(false);
    }
  }

  if (hidden) return null;

  // ─── Launcher button ───────────────────────────────────────────────
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={s('launcher', lang)}
        title={s('launcher', lang)}
        className="fixed bottom-5 left-5 z-[60] flex items-center justify-center
                   h-12 w-12 rounded-full bg-zinc-900 text-cyan-300
                   shadow-lg shadow-black/20 ring-1 ring-cyan-500/30
                   hover:bg-zinc-800 hover:text-cyan-200 transition-colors"
      >
        <MessageSquare size={20} />
      </button>
    );
  }

  // ─── Open modal ────────────────────────────────────────────────────
  const charsLeftSubject = MAX_SUBJECT - subject.length;
  const charsLeftBody    = MAX_BODY    - body.length;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[59] bg-black/40 backdrop-blur-[1px]"
        onClick={() => setOpen(false)}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        className="fixed left-1/2 top-1/2 z-[60] w-[min(540px,calc(100vw-2rem))]
                   -translate-x-1/2 -translate-y-1/2
                   rounded-2xl bg-zinc-900 text-zinc-100 shadow-2xl
                   ring-1 ring-zinc-800 overflow-hidden flex flex-col
                   max-h-[calc(100vh-2rem)]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-800
                        px-4 py-3 bg-zinc-900/80">
          <div className="flex items-center gap-2">
            <MessageSquare size={18} className="text-cyan-400" />
            <h2 id="feedback-title" className="text-sm font-semibold uppercase tracking-wide">
              {s('title', lang)}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={s('cancel', lang)}
            className="rounded-md p-1 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        {done ? (
          <div className="p-6 flex flex-col items-center text-center gap-3">
            <CheckCircle2 size={40} className="text-emerald-400" />
            <h3 className="text-lg font-semibold">{s('thanksTitle', lang)}</h3>
            <p className="text-sm text-zinc-300 max-w-sm">{s('thanksBody', lang)}</p>
            <div className="mt-2 flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setDone(false); }}
                className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              >
                {s('sendAnother', lang)}
              </Button>
              <Button
                type="button"
                onClick={() => setOpen(false)}
                className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950"
              >
                {s('cancel', lang)}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3 p-4 overflow-y-auto">
            <p className="text-xs text-zinc-400 leading-snug">{s('intro', lang)}</p>

            {/* Subject */}
            <label className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wide text-zinc-400">
                {s('subject', lang)}
              </span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value.slice(0, MAX_SUBJECT))}
                placeholder={s('subjectPh', lang)}
                disabled={busy}
                required
                className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2
                           text-sm text-zinc-100 placeholder-zinc-600
                           focus:outline-none focus:border-cyan-500 disabled:opacity-60"
              />
              <span className="text-[10px] text-zinc-600 self-end">
                {charsLeftSubject} {s('charsRemaining', lang)}
              </span>
            </label>

            {/* Message */}
            <label className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wide text-zinc-400">
                {s('message', lang)}
              </span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
                placeholder={s('messagePh', lang)}
                disabled={busy}
                required
                rows={5}
                className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2
                           text-sm text-zinc-100 placeholder-zinc-600 resize-none
                           focus:outline-none focus:border-cyan-500 disabled:opacity-60
                           min-h-[120px] max-h-[260px]"
              />
              <span className="text-[10px] text-zinc-600 self-end">
                {charsLeftBody} {s('charsRemaining', lang)}
              </span>
            </label>

            {/* Email */}
            <label className="flex flex-col gap-1">
              <span className="text-[11px] uppercase tracking-wide text-zinc-400">
                {s('emailLabel', lang)}
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={s('emailPh', lang)}
                disabled={busy}
                className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2
                           text-sm text-zinc-100 placeholder-zinc-600
                           focus:outline-none focus:border-cyan-500 disabled:opacity-60"
              />
              <span className="text-[10px] text-zinc-600 leading-snug">
                {s('emailHint', lang)}
              </span>
            </label>

            {err && (
              <div className="flex items-start gap-2 text-xs text-amber-300
                              bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              >
                {s('cancel', lang)}
              </Button>
              <Button
                type="submit"
                disabled={busy || !subject.trim() || !body.trim()}
                className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 gap-1.5"
              >
                {busy
                  ? (<><Loader2 size={14} className="animate-spin" />{s('sending', lang)}</>)
                  : (<><Send size={14} />{s('send', lang)}</>)
                }
              </Button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
